import moment from 'moment';
import _ from 'lodash';
import LeaveUsageModal from './modals/leave-usage/leave-usage.component';
import LeaveAdjustmentsModal from './modals/leave-adjustments/leave-adjustments.component';

class LeaveController {
  constructor(LeaveService, $scope, $rootScope, AuthenticationService, TimesheetService, $uibModal, $timeout) {
    "ngInject";
    this.name = 'leave';
    this.ptoService = LeaveService;
    this.leaveData = {};
    this.pto = {};
    this.authenticationService = AuthenticationService;
    this.timesheetsService = TimesheetService;
    this.$uibModal = $uibModal;
    this.$timeout = $timeout;
    this.leaveStats = {};
    this.selectedLeaveType = null;
    this.activeTabIndex = 0;
    this.currentPage = {};
    this.itemsPerPage = 10;
    this.annualBenefitCurrentPage = {};
    this.annualBenefitItemsPerPage = 3;
    this.cachedAllYearsStats = {};
    this.cachedStatsYear = {};
    this.chartData = { labels: [], data: [] };
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: false
          }
        }]
      },
      legend: {
        display: false
      },
      elements: {
        line: {
          tension: 0.4
        }
      }
    };

    this.heatMapConfig = {
      start: moment().toDate(),
      domain: "month",
      subDomain: "x_day",
      cellSize: 20,
      range: 1,
      displayLegend: false,
      subDomainTextFormat: "%d",
      legend: [2, 4, 6, 8, 10],
      weekStartOnMonday: false,
      label: {
        position: "top"
      },
      itemName: "hour"
    };

    this.ptoService.refreshPto();

    // When the leaveTypes on ptoservice changes, set the first one as selected
    let leaveTypesWatchInitialized = false;
    $scope.$watch(() => this.ptoService.leaveTypes, (newVal, oldVal) => {
      // Only process if the array reference actually changed
      if (newVal === oldVal && leaveTypesWatchInitialized) return;
      leaveTypesWatchInitialized = true;
      
      if (!newVal || newVal.length === 0) {
        this.leaveStats = {};
        this.selectedLeaveType = null;
        this.cachedAllYearsStats = {};
        this.cachedStatsYear = {};
      } else {
        // Set the first leave type as selected if none is selected
        if (!this.selectedLeaveType || !newVal.find(lt => lt.id === this.selectedLeaveType.id)) {
          // Use selectLeaveType to properly initialize the first tab
          this.$timeout(() => {
            this.selectLeaveType(newVal[0], 0);
          }, 0);
        } else {
          // Defer update to next digest cycle to prevent infinite loops
          this.$timeout(() => {
            this.updateLeaveStatsForSelectedType();
          }, 0);
        }
      }
    });

    // Watch for timesheet changes to update leave diffs (if timesheet is loaded)
    // This allows the leave page to reflect unsaved changes from the timesheet
    $scope.$watch(() => this.timesheetsService.currentTimesheet, (newVal, oldVal) => {
      if (!newVal && !oldVal) return;

      // Update leave diffs based on current timesheet
      this.ptoService.updateTimesheetLeaveDiffs(newVal, this.timesheetsService.persistedTimesheet);

      // Recalculate stats if we have leave types
      if (!this.ptoService.leaveTypes || !this.selectedLeaveType) return;
      
      // Clear cache when timesheet changes as it affects the stats
      if (this.selectedLeaveType) {
        this.cachedAllYearsStats[this.selectedLeaveType.id] = null;
        this.cachedStatsYear[this.selectedLeaveType.id] = null;
      }
      
      // Defer update to prevent infinite loops
      this.$timeout(() => {
        this.updateLeaveStatsForSelectedType();
      }, 0);
    }, true);
  }

  /**
   * Update leave stats for the currently selected leave type
   */
  updateLeaveStatsForSelectedType() {
    if (!this.selectedLeaveType) {
      if (Object.keys(this.leaveStats).length > 0) {
        this.leaveStats = {};
      }
      this.chartData = { labels: [], data: [] };
      return;
    }
    // Always get fresh stats - the service method returns a new object each time
    // We need to assign it directly, but we'll use $timeout to defer this call
    // to prevent infinite digest loops
    const newStats = this.ptoService.calculateLeaveTypePtoStats(this.selectedLeaveType);
    this.leaveStats = newStats;
    
    // Update chart data
    this.updateChartData();
  }
  
  /**
   * Update chart data for the currently selected leave type
   */
  updateChartData() {
    if (!this.selectedLeaveType) {
      this.chartData = { labels: [], data: [] };
      return;
    }
    
    const allBenefits = this.getAnnualBenefitByYear();
    // Sort by year ascending for chart display
    const sortedBenefits = [...allBenefits].sort((a, b) => parseInt(a.selectedYear) - parseInt(b.selectedYear));
    
    const newLabels = sortedBenefits.map(yearStats => yearStats.selectedYear.toString());
    const newData = sortedBenefits.map(yearStats => parseFloat(yearStats.timeOffBenefit));
    
    // Only update if data actually changed to prevent digest loops
    const labelsStr = JSON.stringify(newLabels);
    const dataStr = JSON.stringify(newData);
    const currentLabelsStr = JSON.stringify(this.chartData.labels);
    const currentDataStr = JSON.stringify(this.chartData.data);
    
    if (labelsStr !== currentLabelsStr || dataStr !== currentDataStr) {
      this.chartData = {
        labels: newLabels,
        data: newData
      };
    }
  }

  /**
   * Select a leave type (called when tab changes)
   * @param {Object} leaveType - The leave type to select
   * @param {number} index - The index of the tab
   */
  selectLeaveType(leaveType, index) {
    // Prevent unnecessary updates if already selected
    if (this.selectedLeaveType && this.selectedLeaveType.id === leaveType.id && this.activeTabIndex === index) {
      return;
    }
    this.selectedLeaveType = leaveType;
    this.activeTabIndex = index;
    
    // Initialize pagination for this leave type if not exists
    if (!this.currentPage[leaveType.id]) {
      this.currentPage[leaveType.id] = 1;
    }
    if (!this.annualBenefitCurrentPage[leaveType.id]) {
      this.annualBenefitCurrentPage[leaveType.id] = 1;
    }
    
    // Update stats for the selected leave type
    this.updateLeaveStatsForSelectedType();
  }

  /**
   * Get the display name for a leave type
   * @param {Object} leaveType - The leave type
   * @returns {string} Display name
   */
  getLeaveTypeName(leaveType) {
    return leaveType.leaveTypeName || 'Leave';
  }
  
  /**
   * Get the description for a leave type
   * @param {Object} leaveType - The leave type
   * @returns {string} Description
   */
  getLeaveTypeDescription(leaveType) {
    return leaveType.leaveTypeDescription || '';
  }

  /**
   * Recalculate the leave stats when the selected leave year changes.
   *
   * @param {*} leaveType
   */
  leaveYearSelected(leaveType) {
    this.leaveStats = this.ptoService.calculateLeaveTypePtoStats(leaveType);
  }

  getLeaveYearOptions(leaveType) {
    let leaveYears = [];
    _.forEach(leaveType.leaveHoursByYear, function(data, year) {
      leaveYears.push(Number(year));
    });

    return leaveYears;
  }

  getHeatMapConfig(month) {
    this.heatMapConfig.start = moment().month(month).toDate();
    return this.heatMapConfig;
  }

  isCurrentYear(year) {
    return parseInt(moment().year()) === parseInt(year);
  }

  hasLeaveBenefits() {
    return this.authenticationService.user && this.authenticationService.user.userType === 'fullTime';
  }

  thisYear() {
    return moment().year();
  }

  prevYear() {
    return moment().subtract(1, 'year').year();
  }

  /**
   * Get leave statistics for all years
   * @returns {Array} Array of leave stats objects, one for each year
   */
  getAllYearsLeaveStats() {
    if (!this.selectedLeaveType) {
      return [];
    }

    const leaveType = this.selectedLeaveType;
    const currentYear = leaveType.selectedLeaveYear;
    const cacheKey = leaveType.id;
    
    // Check if we can use cached results
    if (this.cachedAllYearsStats[cacheKey] && this.cachedStatsYear[cacheKey] === currentYear) {
      return this.cachedAllYearsStats[cacheKey];
    }
    
    const years = this.getLeaveYearOptions(leaveType);
    
    // Sort years in ascending order (oldest first)
    const sortedYears = years.sort((a, b) => a - b);
    
    // Calculate stats for each year
    const allYearsStats = sortedYears.map(year => {
      // Temporarily set the selected year to calculate stats for that year
      const originalSelectedYear = leaveType.selectedLeaveYear;
      leaveType.selectedLeaveYear = year;
      
      const stats = this.ptoService.calculateLeaveTypePtoStats(leaveType, year);
      
      // Restore original selected year
      leaveType.selectedLeaveYear = originalSelectedYear;
      
      return stats;
    });
    
    // Cache the results
    this.cachedAllYearsStats[cacheKey] = allYearsStats;
    this.cachedStatsYear[cacheKey] = currentYear;
    
    return allYearsStats;
  }

  /**
   * Get paginated leave statistics for all years
   * @returns {Array} Array of leave stats objects for the current page
   */
  getPaginatedYearsLeaveStats() {
    if (!this.selectedLeaveType) return [];
    const allStats = this.getAllYearsLeaveStats();
    const page = this.currentPage[this.selectedLeaveType.id] || 1;
    const startIndex = (page - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return allStats.slice(startIndex, endIndex);
  }

  /**
   * Get total number of pages
   * @returns {number} Total number of pages
   */
  getTotalPages() {
    if (!this.selectedLeaveType) return 0;
    const allStats = this.getAllYearsLeaveStats();
    return Math.ceil(allStats.length / this.itemsPerPage);
  }

  /**
   * Get current page number
   * @returns {number} Current page number
   */
  getCurrentPage() {
    if (!this.selectedLeaveType) return 1;
    return this.currentPage[this.selectedLeaveType.id] || 1;
  }

  /**
   * Go to next page
   */
  nextPage() {
    if (!this.selectedLeaveType) return;
    const totalPages = this.getTotalPages();
    const currentPage = this.getCurrentPage();
    if (currentPage < totalPages) {
      this.currentPage[this.selectedLeaveType.id] = currentPage + 1;
    }
  }

  /**
   * Go to previous page
   */
  prevPage() {
    if (!this.selectedLeaveType) return;
    const currentPage = this.getCurrentPage();
    if (currentPage > 1) {
      this.currentPage[this.selectedLeaveType.id] = currentPage - 1;
    }
  }

  /**
   * Go to specific page
   * @param {number} page - Page number to navigate to
   */
  goToPage(page) {
    if (!this.selectedLeaveType) return;
    const totalPages = this.getTotalPages();
    if (page >= 1 && page <= totalPages) {
      this.currentPage[this.selectedLeaveType.id] = page;
    }
  }

  /**
   * Get annual benefit by year, sorted descending and excluding current year
   * @returns {Array} Array of year stats objects sorted by year descending, excluding current year
   */
  getAnnualBenefitByYear() {
    if (!this.selectedLeaveType) return [];
    const allStats = this.getAllYearsLeaveStats();
    const currentYear = parseInt(moment().year());
    
    // Filter out current year and sort by year descending
    return allStats
      .filter(yearStats => parseInt(yearStats.selectedYear) !== currentYear)
      .sort((a, b) => parseInt(b.selectedYear) - parseInt(a.selectedYear));
  }

  /**
   * Get chart data for annual benefit line chart
   * @returns {Object} Chart data object with labels and data arrays
   * Returns cached chartData to prevent infinite digest loops
   */
  getAnnualBenefitChartData() {
    return this.chartData;
  }

  /**
   * Get chart options for annual benefit line chart
   * @returns {Object} Chart options
   * Returns cached chartOptions to prevent infinite digest loops
   */
  getAnnualBenefitChartOptions() {
    return this.chartOptions;
  }

  /**
   * Get paginated annual benefit by year
   * @returns {Array} Array of year stats objects for the current page
   */
  getPaginatedAnnualBenefitByYear() {
    if (!this.selectedLeaveType) return [];
    const allBenefits = this.getAnnualBenefitByYear();
    const page = this.annualBenefitCurrentPage[this.selectedLeaveType.id] || 1;
    const startIndex = (page - 1) * this.annualBenefitItemsPerPage;
    const endIndex = startIndex + this.annualBenefitItemsPerPage;
    return allBenefits.slice(startIndex, endIndex);
  }

  /**
   * Get total number of pages for annual benefit table
   * @returns {number} Total number of pages
   */
  getAnnualBenefitTotalPages() {
    if (!this.selectedLeaveType) return 0;
    const allBenefits = this.getAnnualBenefitByYear();
    return Math.ceil(allBenefits.length / this.annualBenefitItemsPerPage);
  }

  /**
   * Get current page for annual benefit
   * @returns {number} Current page number
   */
  getAnnualBenefitCurrentPage() {
    if (!this.selectedLeaveType) return 1;
    return this.annualBenefitCurrentPage[this.selectedLeaveType.id] || 1;
  }

  /**
   * Go to next page for annual benefit table
   */
  annualBenefitNextPage() {
    if (!this.selectedLeaveType) return;
    const totalPages = this.getAnnualBenefitTotalPages();
    const currentPage = this.getAnnualBenefitCurrentPage();
    if (currentPage < totalPages) {
      this.annualBenefitCurrentPage[this.selectedLeaveType.id] = currentPage + 1;
    }
  }

  /**
   * Go to previous page for annual benefit table
   */
  annualBenefitPrevPage() {
    if (!this.selectedLeaveType) return;
    const currentPage = this.getAnnualBenefitCurrentPage();
    if (currentPage > 1) {
      this.annualBenefitCurrentPage[this.selectedLeaveType.id] = currentPage - 1;
    }
  }

  /**
   * Open the leave usage modal for a specific year
   * @param {Object} yearStats - The year stats object for the selected year
   */
  openLeaveUsageModal(yearStats) {
    const modalConfig = Object.assign({}, LeaveUsageModal, {
      resolve: {
        yearStats: () => yearStats
      }
    });
    
    const modalInstance = this.$uibModal.open(modalConfig);

    modalInstance.result.catch(() => {
      // Modal was dismissed
    });
  }

  /**
   * Check if year has adjustments (non-zero)
   * @param {Object} yearStats - The year stats object
   * @returns {boolean} True if adjustments exist
   */
  hasAdjustments(yearStats) {
    return parseFloat(yearStats.leaveAdjustments) !== 0;
  }

  /**
   * Open the leave adjustments modal for a specific year
   * @param {Object} yearStats - The year stats object for the selected year
   */
  openLeaveAdjustmentsModal(yearStats) {
    if (!this.hasAdjustments(yearStats) || !this.selectedLeaveType) {
      return;
    }
    
    const modalConfig = Object.assign({}, LeaveAdjustmentsModal, {
      resolve: {
        yearStats: () => yearStats,
        leaveType: () => this.selectedLeaveType
      }
    });
    
    const modalInstance = this.$uibModal.open(modalConfig);

    modalInstance.result.catch(() => {
      // Modal was dismissed
    });
  }
}

export default LeaveController;




// WEBPACK FOOTER //
// ./client/pages/leave/leave.controller.js